import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type StudyTab = "summary" | "deck" | "chat" | "analyst" | "survey";

interface StudyTabBarProps {
  activeTab: StudyTab;
  onTabChange: (tab: StudyTab) => void;
  hiddenTabs?: Array<StudyTab>;
  disabledTabs?: Array<StudyTab>;
  disabledTabTooltips?: Partial<Record<StudyTab, string>>;
  className?: string;
}

export function StudyTabBar({
  activeTab,
  onTabChange,
  hiddenTabs = [],
  disabledTabs = [],
  disabledTabTooltips = {},
  className,
}: StudyTabBarProps) {
  const { t } = useTranslation("app");
  const tabs: Array<{ id: StudyTab; label: string }> = [
    { id: "summary", label: t("studyReport.tabSummary") },
    { id: "deck", label: t("studyReport.tabDeck") },
    { id: "chat", label: t("studyReport.tabChat") },
    { id: "analyst", label: t("studyReport.tabAnalyst") },
    { id: "survey", label: t("studyReport.tabSurvey") },
  ];
  const visibleTabs = tabs.filter((tab) => !hiddenTabs.includes(tab.id));

  return (
    <motion.div
      layoutId="study-tab-bar"
      layout
      className={cn("flex items-center gap-2", className)}
    >
      {visibleTabs.map((tab) => {
        const isDisabled = disabledTabs.includes(tab.id);
        const tooltip = disabledTabTooltips[tab.id];
        const button = (
          <button
            type="button"
            onClick={() => {
              if (isDisabled) {
                return;
              }
              onTabChange(tab.id);
            }}
            disabled={isDisabled}
            data-testid={`study-tab-${tab.id}`}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap",
              isDisabled
                ? "cursor-not-allowed border border-border/60 text-foreground/40 opacity-60"
                : activeTab === tab.id
                  ? "bg-cta text-cta-foreground"
                  : "border border-border/60 text-foreground/70 hover:text-foreground hover:border-border",
            )}
          >
            {tab.label}
          </button>
        );

        if (!isDisabled || !tooltip) {
          return (
            <span key={tab.id} className="inline-flex">
              {button}
            </span>
          );
        }

        return (
          <TooltipProvider key={tab.id} delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex" title={tooltip}>
                  {button}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px] text-center">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </motion.div>
  );
}
